import {useState, type ReactNode} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {
  Clock,
  Zap,
  ArrowUpRight,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Filter
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Progress,
  ProgressLabel,
  ProgressValue,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  buttonVariants
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useSubmissionStats,
  useSubmissionDeliveryStats,
  useDashboardHoppers,
  useQcPolicyViolations,
  useModerationPolicyViolations,
  useModerationQueueSettings,
  useModerationSettings
} from '../api/hooks';
import {useAuth} from '../context/useAuth';
import {type PolicyViolationStat, type DeliveryGranularity} from '../api/submissions';
import {type HopperDashboardSummary, type HopperTargetPeriod} from '../api/hopper-configs';
import {cn} from '../lib/utils';
import {targetDelta} from '../lib/targetDelta';
import {toneBadge} from '../lib/status';
import {DeliveryCharts, BUCKET_COUNT} from '../components/dashboard/DeliveryCharts';
import {VERTICAL_GRID} from '../components/dashboard/verticalGrid';
import {QaFeedbackWidget} from '../components/dashboard/QaFeedbackWidget';
import {ReviewerCommentsWidget} from '../components/dashboard/ReviewerCommentsWidget';
import {useUiStore} from '../store/uiStore';
import {PageHeaderActions} from '@/components/layout/PageHeader';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';

function timeGreeting(name: string): string {
  const h = new Date().getHours();
  return `${h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'}, ${name}`;
}

// The policy violations the dashboard has always listed first, in this order. They used to be the
// keys of a colour palette; the palette is gone (the design system colours cards and charts
// itself) but the familiar ordering stays, with any other deliverable violation sorted after them.
const KNOWN_POLICY_VIOLATIONS = [
  'Hate',
  'Suicide & Self Harm',
  'Child Safety',
  'Violent Extremism',
  'Harassment',
  'Harmful & Dangerous',
  'Adult',
  'Graphic Violence'
];

// A hopper's target is named for its period ('weekly'), while the chart endpoint buckets by unit
// ('week'). One table joins the two so neither side has to know the other's spelling.
const GRANULARITY: Record<HopperTargetPeriod, DeliveryGranularity> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month'
};

const RANGE_LABEL: Record<HopperTargetPeriod, string> = {
  daily: 'Last 30 days',
  weekly: 'Last 12 weeks',
  monthly: 'Last 12 months'
};

const PERIOD_VIEWS: ReadonlyArray<{value: HopperTargetPeriod; label: string}> = [
  {value: 'daily', label: 'Daily'},
  {value: 'weekly', label: 'Weekly'},
  {value: 'monthly', label: 'Monthly'}
];

function isPeriodView(value: unknown): value is HopperTargetPeriod {
  return value === 'daily' || value === 'weekly' || value === 'monthly';
}

export function DashboardPage() {
  const {user} = useAuth();
  const [submissionType, setSubmissionType] = useState<'Actor' | 'Content'>('Content');
  // A view choice, not a config change: every card reads the number already stored for the selected
  // period. Nothing here writes back, so switching views can't drift a client's actual targets. A
  // hopper stores all three numbers and no period of its own - which one matters is read from here.
  const [periodView, setPeriodView] = useState<HopperTargetPeriod>('weekly');
  // The verticals the analyst has unticked, rather than the ones ticked: the list of deliverable
  // verticals changes with the Actor/Content toggle and with hopper config, and a newly deliverable
  // vertical should appear rather than stay hidden behind a selection made before it existed.
  // Persisted per browser so a reload re-shows the same selection.
  const hiddenVerticals = useUiStore((s) => s.dashboardHiddenVerticals);
  const setHiddenVerticals = useUiStore((s) => s.setDashboardHiddenVerticals);

  // The dashboard shows every vertical, not only the ones the analyst is granted: the numbers are for
  // resourcing, and the lists, items and exports behind them stay scoped to the grant. Each request
  // below asks for the unscoped aggregate explicitly.
  const stats = useSubmissionStats(true, submissionType, undefined, undefined, true).data ?? null;
  // The series is bucketed by whatever period is in view: daily totals are meaningless beside a
  // monthly target, and rolling day rows up client-side would need the Monday-start rule duplicated
  // in two places, so the API buckets and this just asks for the granularity it wants.
  const granularity = GRANULARITY[periodView];
  const deliveryStats =
    useSubmissionDeliveryStats(granularity, BUCKET_COUNT[granularity], true, submissionType, true)
      .data ?? [];
  const hoppers = useDashboardHoppers().data ?? [];
  const qcPolicyViolations = useQcPolicyViolations(true).data ?? [];
  // Case-insensitive, as the API compares: a vertical outside the grant still gets its numbers, but
  // its card can't open a submissions list that would come back empty.
  const grantedVerticals = user?.allowedVerticals?.map((v) => v.toLowerCase()) ?? null;
  const canOpenVertical = (v: string) => grantedVerticals?.includes(v.toLowerCase()) ?? true;

  // Policy violations can have separate Actor and Content hoppers - only the one matching the
  // toggled type should drive this policy violation's target bar, since each hopper's batch - and the
  // target it is measured against - is type-scoped.
  const hopperByPolicyViolation = Object.fromEntries(
    hoppers
      .filter((h) => h.submission_type === submissionType)
      .map((h) => [h.policy_violation, h] as const)
  );
  const statByPolicyViolation = Object.fromEntries(
    (stats?.by_policy_violation ?? []).map((v) => [v.policy_violation, v] as const)
  );
  // Scoped to the toggled type like every other number on the card; `pending` is both types summed.
  const qcPendingByPolicyViolation = Object.fromEntries(
    qcPolicyViolations.map(
      (v) =>
        [
          v.policy_violation,
          submissionType === 'Actor' ? v.pending_actor : v.pending_content
        ] as const
    )
  );
  const firstName = user?.displayName?.split(' ')[0] ?? 'there';
  // The queue section is for whoever can work the queue, once it is switched on.
  const queuesEnabled = useModerationSettings().data?.queues_enabled === true;
  const showQueues = queuesEnabled && (user?.permissions.includes('access:queues') ?? false);

  // A policy violation earns a widget only once it has somewhere to deliver to - a hopper for the
  // toggled submission type whose target actually resolves (scheduled batch or direct send, webhook
  // or sheet). A card for a violation with no deliverable is a dead end for the analyst. The
  // familiar names keep their order; a deliverable violation outside that list still shows, after
  // them.
  const deliverablePolicyViolations = new Set(
    hoppers
      .filter((h) => h.submission_type === submissionType && h.is_deliverable)
      .map((h) => h.policy_violation)
  );
  const knownPolicyViolations = KNOWN_POLICY_VIOLATIONS.filter((v) =>
    deliverablePolicyViolations.has(v)
  );
  const extraPolicyViolations = [...deliverablePolicyViolations]
    .filter((v) => !KNOWN_POLICY_VIOLATIONS.includes(v))
    .sort();
  const allPolicyViolations = [...knownPolicyViolations, ...extraPolicyViolations];
  const visiblePolicyViolations = allPolicyViolations.filter((v) => !hiddenVerticals.has(v));

  const policyViolationRows: Array<{
    stat: PolicyViolationStat;
    hopper: HopperDashboardSummary | null;
  }> = visiblePolicyViolations.map((v) => ({
    stat: statByPolicyViolation[v] ?? {
      policy_violation: v,
      total: 0,
      in_hopper: 0,
      sent: 0,
      sent_today: 0,
      sent_this_week: 0,
      sent_this_month: 0
    },
    hopper: hopperByPolicyViolation[v] ?? null
  }));

  // Each chart's target line is its hopper's number for the period in view - the same number the
  // matching bar on its card is measured against.
  const chartTargets = Object.fromEntries(
    visiblePolicyViolations.map(
      (v) => [v, hopperByPolicyViolation[v]?.[`${periodView}_target`] ?? 0] as const
    )
  );

  return (
    <div className="h-full w-full overflow-y-auto">
      {/* The QA feedback widget below shows item thumbnails. */}
      <PageHeaderActions>
        <SensitiveMediaToggle />
      </PageHeaderActions>
      <div className="mx-auto max-w-screen-2xl space-y-8 px-4 py-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-muted-foreground text-xs">
              {new Date().toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
            <h2 className="font-heading mt-1 text-lg font-medium tracking-tight">
              {timeGreeting(firstName)}
            </h2>
          </div>
          {/* The filters scope every section below - targets, chart and queues - so they sit above
              all three rather than in the page header, where they read as page chrome. */}
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              aria-label="Submission type"
              value={[submissionType]}
              onValueChange={(next) => {
                const [type] = next;
                if (type === 'Actor' || type === 'Content') {
                  setSubmissionType(type);
                }
              }}>
              <ToggleGroupItem value="Actor">Actor</ToggleGroupItem>
              <ToggleGroupItem value="Content">Content</ToggleGroupItem>
            </ToggleGroup>
            <VerticalFilter
              options={allPolicyViolations}
              hidden={hiddenVerticals}
              onChange={setHiddenVerticals}
            />
          </div>
        </div>

        {/* QA False Positive Feedback Widget - renders nothing when there's no outstanding feedback */}
        <QaFeedbackWidget />

        {/* Reviewer comments left on this analyst's submissions - also renders nothing when empty */}
        <ReviewerCommentsWidget />

        {/* Target Status Grid Section */}
        <section>
          <SectionHead
            title="Targets"
            action={
              <Link to="/submissions" className={buttonVariants({variant: 'link', size: 'sm'})}>
                All submissions
                <ArrowUpRight />
              </Link>
            }
          />

          {policyViolationRows.length > 0 ? (
            <div className={cn(VERTICAL_GRID, 'items-stretch')}>
              {policyViolationRows.map(({stat, hopper}) => (
                <PolicyViolationCard
                  key={stat.policy_violation}
                  stat={stat}
                  hopper={hopper}
                  qcPending={qcPendingByPolicyViolation[stat.policy_violation] ?? 0}
                  canOpen={canOpenVertical(stat.policy_violation)}
                />
              ))}
            </div>
          ) : allPolicyViolations.length > 0 ? (
            <p className="text-muted-foreground text-xs">No verticals selected.</p>
          ) : (
            <Empty className="border py-8">
              <EmptyHeader>
                <EmptyTitle>No {submissionType.toLowerCase()} targets</EmptyTitle>
                <EmptyDescription>
                  No {submissionType.toLowerCase()} target has a delivery destination configured
                  yet.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </section>

        {/* Delivery chart section - nothing deliverable means nothing to chart. With every vertical
            unticked the heading and period toggle stay put, so the layout doesn't jump. */}
        {allPolicyViolations.length > 0 ? (
          <section>
            <SectionHead
              title="Submissions"
              aside={RANGE_LABEL[periodView]}
              action={
                <ToggleGroup
                  variant="outline"
                  spacing={0}
                  size="sm"
                  aria-label="Chart period"
                  value={[periodView]}
                  onValueChange={(next) => {
                    const [view] = next;
                    if (isPeriodView(view)) {
                      setPeriodView(view);
                    }
                  }}>
                  {PERIOD_VIEWS.map((view) => (
                    <ToggleGroupItem
                      key={view.value}
                      value={view.value}
                      title={`Bucket deliveries by ${view.value} period`}>
                      {view.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              }
            />
            {visiblePolicyViolations.length > 0 ? (
              <DeliveryCharts
                data={deliveryStats}
                policyViolations={visiblePolicyViolations}
                targets={chartTargets}
                granularity={granularity}
                submissionType={submissionType}
              />
            ) : null}
          </section>
        ) : null}

        {showQueues ? (
          <ModerationQueues submissionType={submissionType} hiddenVerticals={hiddenVerticals} />
        ) : null}
      </div>
    </div>
  );
}

// ── Supporting Components ───────────────────────────────────────────────────

// Work waiting in the moderation queue now, scoped by the dashboard's filters: Actor reads the
// channel queue and Content the video one, and an unticked vertical is left out. The counts are
// the analyst's own queue (the verticals they are granted), so each row opens straight into it.
function ModerationQueues({
  submissionType,
  hiddenVerticals
}: {
  readonly submissionType: 'Actor' | 'Content';
  readonly hiddenVerticals: ReadonlySet<string>;
}) {
  const kind = submissionType === 'Actor' ? 'channel' : 'video';
  const {data, isLoading, isError} = useModerationPolicyViolations(false, undefined, kind);
  const channelReview = useModerationQueueSettings().data?.channel_review_enabled !== false;
  const queues = (data ?? []).filter((q) => !hiddenVerticals.has(q.policy_violation));
  const pending = queues.reduce((sum, q) => sum + q.available, 0);
  const highRisk = queues.reduce((sum, q) => sum + q.high_risk, 0);
  const waiting = queues.filter((q) => q.available > 0);
  const tiles = [
    {label: 'Pending review', value: pending, hint: 'Items across all queues'},
    {label: 'High risk', value: highRisk, hint: 'Rated high risk by the engine'}
  ];

  return (
    <section aria-label="Moderation queues">
      <SectionHead
        title="Moderation queues"
        action={
          <Link
            to="/reporting/queue_analytics"
            className={buttonVariants({variant: 'link', size: 'sm'})}>
            Queue analytics
            <ArrowUpRight />
          </Link>
        }
      />

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Couldn&apos;t load the queues</AlertTitle>
          <AlertDescription>Refresh the page to try again.</AlertDescription>
        </Alert>
      ) : submissionType === 'Actor' && !channelReview ? (
        <Empty className="border py-6">
          <EmptyHeader>
            <EmptyTitle>Channel review is off</EmptyTitle>
            <EmptyDescription>
              Channels are not being queued for review. Switch to Content to see the video queue.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {tiles.map((tile) => (
            <Card key={tile.label} size="sm">
              <CardHeader>
                <CardDescription>{tile.label}</CardDescription>
                <CardTitle className="text-2xl font-semibold tracking-tight tabular-nums">
                  {isLoading ? '—' : tile.value.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">{tile.hint}</CardContent>
            </Card>
          ))}

          {isLoading ? null : waiting.length === 0 ? (
            <Empty className="border py-6 sm:col-span-2">
              <EmptyHeader>
                <EmptyTitle>Nothing waiting</EmptyTitle>
                <EmptyDescription>Every queue is clear for now.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Card size="sm" className="sm:col-span-2">
              <CardHeader>
                <CardTitle>By policy violation</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="flex flex-col gap-2">
                  {waiting.map((queue) => (
                    <li
                      key={queue.policy_violation}
                      className="flex items-center justify-between gap-3">
                      <Link
                        to={`/queues?vertical=${encodeURIComponent(queue.policy_violation)}`}
                        className="min-w-0 truncate hover:underline">
                        {queue.policy_violation}
                      </Link>
                      <span className="flex shrink-0 items-center gap-2 tabular-nums">
                        {queue.high_risk > 0 ? (
                          <Badge {...toneBadge('destructive')}>{queue.high_risk} high risk</Badge>
                        ) : null}
                        <span className="font-medium">{queue.available.toLocaleString()}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

// Multi-select over the deliverable verticals. Controlled by the set the analyst has *hidden*, so a
// vertical that becomes deliverable later shows up ticked.
export function VerticalFilter({
  options,
  hidden,
  onChange
}: {
  readonly options: readonly string[];
  readonly hidden: ReadonlySet<string>;
  readonly onChange: (next: ReadonlySet<string>) => void;
}) {
  const shown = options.filter((v) => !hidden.has(v));
  const allShown = shown.length === options.length;
  const [onlyShown] = shown;
  const label = allShown
    ? 'All verticals'
    : shown.length > 1
      ? `${shown.length} verticals`
      : (onlyShown ?? 'No verticals');
  // Base UI checkbox items leave the menu open after a pick (closeOnClick defaults to false), so
  // several verticals can be toggled in one visit.

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Filter by vertical, ${label.toLowerCase()}`}
        disabled={options.length === 0}
        render={
          <Button
            variant="secondary"
            size="sm"
            className={cn(!allShown && 'ring-primary ring-1 ring-inset')}
          />
        }>
        <Filter />
        <span className="max-w-40 truncate">{label}</span>
        <ChevronDown />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Policy violation</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={allShown}
            onCheckedChange={() => {
              onChange(allShown ? new Set(options) : new Set());
            }}>
            All verticals
          </DropdownMenuCheckboxItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {options.map((v) => (
            <DropdownMenuCheckboxItem
              key={v}
              checked={!hidden.has(v)}
              onCheckedChange={() => {
                const next = new Set(hidden);
                if (next.has(v)) {
                  next.delete(v);
                } else {
                  next.add(v);
                }

                onChange(next);
              }}>
              {v}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SectionHead({
  title,
  aside,
  action
}: {
  readonly title: string;
  readonly aside?: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-baseline gap-2">
        <h3 className="font-heading text-sm font-medium">{title}</h3>
        {aside ? <span className="text-muted-foreground text-xs">{aside}</span> : null}
      </div>
      {action}
    </div>
  );
}

function TargetProgress({
  label,
  sent,
  target
}: {
  readonly label: string;
  readonly sent: number;
  readonly target: number;
}) {
  const pct = Math.min(100, Math.round((sent / target) * 100));
  const delta = targetDelta(sent, target);
  // The bar shows how far along, the tooltip how far off: the number still to send is what an
  // analyst actually plans against, and a bar can't say it without a second label crowding the row.
  return (
    <Tooltip>
      <TooltipTrigger render={<div />}>
        <Progress value={pct} aria-label={label} className="gap-x-3 gap-y-1">
          <ProgressLabel className="text-muted-foreground">{label}</ProgressLabel>
          <ProgressValue className="text-foreground font-medium tabular-nums">
            {() => `${sent.toLocaleString()} / ${target.toLocaleString()}`}
          </ProgressValue>
        </Progress>
      </TooltipTrigger>
      <TooltipContent side="top">
        <span className="grid gap-0.5">
          <span>
            Sent {sent.toLocaleString()} · Target {target.toLocaleString()}
          </span>
          <span className="font-medium">
            {delta.label} {delta.value}
          </span>
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

export function PolicyViolationCard({
  stat,
  hopper,
  qcPending,
  canOpen = true
}: {
  readonly stat: PolicyViolationStat;
  readonly hopper: HopperDashboardSummary | null;
  readonly qcPending: number;
  // False for a vertical outside the analyst's grant: they see its numbers but can't open its items.
  readonly canOpen?: boolean;
}) {
  const navigate = useNavigate();
  const open = () => {
    if (!canOpen) {
      return;
    }

    void navigate(`/submissions?policy_violation=${encodeURIComponent(stat.policy_violation)}`);
  };

  const isScheduled = Boolean(hopper?.schedule);
  const isEnabled = hopper?.enabled ?? false;
  const batchSize = hopper?.batch_size ?? 0;
  const fillPct = batchSize > 0 ? Math.min(100, Math.round((stat.in_hopper / batchSize) * 100)) : 0;

  // All three targets show together, always - there is no "period in view" at the card level any
  // more, only on the chart below (driven by the dashboard's own toggle). Nothing is divided or
  // multiplied: a monthly commitment is measured against a calendar month of sends, which is the
  // whole reason all three numbers are stored rather than derived. A period with no number falls
  // back to the bare sent count rather than a bar with no denominator.
  const targetRows = (
    [
      ['Daily', stat.sent_today, hopper?.daily_target ?? 0],
      ['Weekly', stat.sent_this_week, hopper?.weekly_target ?? 0],
      ['Monthly', stat.sent_this_month, hopper?.monthly_target ?? 0]
    ] as const
  ).map(([name, sent, target]) => ({name, sent, target}));

  // A direct/unconfigured card's Hopper count is only worth showing when it's nonzero - hides the
  // dead "0" tile without hiding a real backlog waiting to be fired manually. Today/Weekly always
  // anchor the bottom row; the top row is In QC alone (spanning full width) or In QC + Hopper.
  const showQueued = isScheduled || stat.in_hopper > 0;
  const tiles = [
    {label: 'In QC', value: qcPending, wide: !showQueued},
    ...(showQueued ? [{label: 'Hopper', value: stat.in_hopper, wide: false}] : []),
    {label: 'Today', value: stat.sent_today, wide: false},
    {label: 'Weekly', value: stat.sent_this_week, wide: false}
  ];

  return (
    <Card
      interactive={canOpen}
      size="sm"
      role={canOpen ? 'link' : undefined}
      className="h-full"
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          open();
        }
      }}>
      <CardHeader>
        <CardTitle className="truncate">{stat.policy_violation}</CardTitle>
        {hopper ? (
          <CardAction>
            {/* The delivery mode is a metadata chip on a card, so it takes the card's tag tint;
                a paused schedule drops to the plain outline so the two states still read apart. */}
            {isScheduled ? (
              <Badge variant={isEnabled ? 'tag' : 'outline'}>
                <Clock />
                {isEnabled ? 'Scheduled' : 'Paused'}
              </Badge>
            ) : (
              <Badge variant="tag">
                <Zap />
                Direct
              </Badge>
            )}
          </CardAction>
        ) : null}
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        {/* Separate rounded tiles on the muted fill, set apart by the gap rather than hairlines. */}
        <div className="grid grid-cols-2 gap-2">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className={cn('bg-muted min-w-0 rounded-lg px-3 py-2.5', tile.wide && 'col-span-2')}>
              <p className="truncate text-xl font-semibold tracking-tight tabular-nums">
                {tile.value.toLocaleString()}
              </p>
              <p className="text-muted-foreground mt-1 text-[10px] tracking-widest uppercase">
                {tile.label}
              </p>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {targetRows.map((row) =>
            row.target > 0 ? (
              <TargetProgress
                key={row.name}
                label={`${row.name} target`}
                sent={row.sent}
                target={row.target}
              />
            ) : (
              <div key={row.name} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{row.name}</span>
                <span className="font-medium tabular-nums">{row.sent.toLocaleString()}</span>
              </div>
            )
          )}
          {/* Batch fill is not a target - it's how close the next fire is, measured against the
              per-fire row cap - so the only thing said about it is that a batch is ready to go.
              A shortfall count beside a target bar read as a shortfall against the target. */}
          {batchSize > 0 && fillPct >= 100 && isEnabled ? (
            <Badge {...toneBadge('success')}>
              <CheckCircle2 aria-hidden="true" />
              Batch ready for client submission
            </Badge>
          ) : null}
        </div>

        {/* A direct hopper's status is already the top badge's job ("Direct") - this row exists
            for the cases that badge can't say: when a hopper is actually scheduled (its next run,
            or that it's paused), or when there's no hopper at all to configure. */}
        {hopper ? (
          isScheduled ? (
            <p className="text-muted-foreground mt-auto flex items-center gap-1.5 text-xs">
              <Clock className="size-3 shrink-0" aria-hidden="true" />
              <span className="truncate">
                {isEnabled && hopper.next_run_at
                  ? `Runs ${formatDistanceToNow(new Date(hopper.next_run_at), {addSuffix: true})}`
                  : `Paused • ${hopper.schedule}`}
              </span>
            </p>
          ) : null
        ) : (
          <Alert className="bg-card mt-auto">
            <AlertCircle />
            <AlertTitle>Unconfigured target</AlertTitle>
            <AlertDescription>No hopper delivers this policy violation yet.</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
